import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "oppad_cart";

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || { vendorId: null, vendorName: null, items: {} };
  } catch {
    return { vendorId: null, vendorName: null, items: {} };
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(load);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(cart));
  }, [cart]);

  const add = useCallback((vendor, product) => {
    setCart((prev) => {
      // Cart is single-vendor; switching vendors resets it.
      const base =
        prev.vendorId && prev.vendorId !== vendor.vendor_id
          ? { vendorId: vendor.vendor_id, vendorName: vendor.name, items: {} }
          : { vendorId: vendor.vendor_id, vendorName: vendor.name, items: { ...prev.items } };
      const existing = base.items[product.product_id];
      base.items[product.product_id] = {
        product_id: product.product_id,
        name: product.name,
        unit_price: product.sale_price || product.price,
        image_url: product.image_url,
        quantity: (existing?.quantity || 0) + 1,
      };
      return base;
    });
  }, []);

  const setQty = useCallback((productId, quantity) => {
    setCart((prev) => {
      const items = { ...prev.items };
      if (quantity <= 0) delete items[productId];
      else items[productId] = { ...items[productId], quantity };
      const empty = Object.keys(items).length === 0;
      return empty ? { vendorId: null, vendorName: null, items: {} } : { ...prev, items };
    });
  }, []);

  const clear = useCallback(() => setCart({ vendorId: null, vendorName: null, items: {} }), []);

  const list = useMemo(() => Object.values(cart.items), [cart.items]);
  const count = useMemo(() => list.reduce((s, i) => s + i.quantity, 0), [list]);
  const subtotal = useMemo(() => list.reduce((s, i) => s + i.unit_price * i.quantity, 0), [list]);

  const value = useMemo(
    () => ({ ...cart, list, count, subtotal, add, setQty, clear }),
    [cart, list, count, subtotal, add, setQty, clear],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
